import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMyCompany } from "../../api/useMyCompany";
import { useMyBranchName } from "../../api/useMyBranchName";
import { InstallPrompt } from "../controls/InstallPrompt";
import { ControlRail } from "./ControlRail";
import { localName } from "../../lib/localName";
import { useAuth } from "../../context/AuthContext";
import { useEnsureAvatar } from "../../hooks/useEnsureAvatar";
import { initials } from "../../utils/initials";

// brand + identity block, shown on the home page only
export const TopBar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const company = useMyCompany();
  const branch = useMyBranchName();
  const { user } = useAuth();
  const personName = localName(user);
  useEnsureAvatar();

  return (
    <header className="relative min-h-[7.5rem]" data-testid="top-bar">
      <ControlRail />

      <button
        type="button"
        onClick={() => navigate("/settings")}
        data-testid="header-avatar"
        aria-label={personName || "profile"}
        className="absolute right-0 top-0 z-30 flex h-[3.6rem] w-[3.6rem] items-center justify-center overflow-hidden rounded-2xl bg-white/95 text-primary shadow-neu-sm transition hover:scale-105 lg:right-2"
      >
        {user?.avatar ? (
          <img src={user.avatar} alt={personName || "avatar"} className="h-full w-full object-cover" />
        ) : (
          <span className="text-lg font-black" data-testid="header-avatar-initials">{initials(user?.name)}</span>
        )}
      </button>

      <div className="flex flex-col items-center px-16 text-center">
        <h1
          className="w-full break-words bg-gradient-to-b from-foreground via-foreground to-foreground/60 bg-clip-text font-brand text-3xl font-black leading-tight tracking-[-0.01em] text-transparent drop-shadow-[0_2px_12px_rgba(0,0,0,0.25)] sm:text-4xl"
          data-testid="brand-name"
        >
          {company ? company.nickname || company.name : "\u00A0"}
        </h1>
        <span className="mt-1.5 h-[3px] w-14 rounded-full bg-primary" aria-hidden="true" />
        {branch ? (
          <p className="mt-1 text-[13px] font-bold text-primary" data-testid="current-branch-name">
            {branch.name}
            {branch.isHeadOffice ? ` · ${t("branches.headOffice")}` : ""}
          </p>
        ) : null}
        {personName ? (
          <p className="mt-1 text-lg font-black tracking-tight text-primary" data-testid="current-user-name">
            {personName}
          </p>
        ) : null}
        {company?.tagline ? (
          <p className="mt-0.5 text-[13px] font-bold text-amber-400" data-testid="company-tagline">
            {company.tagline}
          </p>
        ) : null}
        <div className="mt-2.5">
          <InstallPrompt />
        </div>
      </div>
    </header>
  );
};
